import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import api, { API, formatApiError } from "../lib/api";
import CalendarView from "../components/CalendarView";
import MessagingPanel from "../components/MessagingPanel";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import SignatureGate from "../components/SignatureGate";
import NotificationToggle from "../components/NotificationToggle";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { GraduationCap, BookOpenCheck, KeyRound, CheckCircle2, Calendar as Cal, Clock, MapPin, ClipboardList, MessageSquare, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const ML = { arabe: "Arabe", coran: "Coran", education_musulmane: "Éd. Musulmane" };

export default function ParentDashboard() {
  const { user } = useAuth();
  const { reenrollmentOpen } = useSettings();
  const [children, setChildren] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [homeworks, setHomeworks] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [activeChild, setActiveChild] = useState(null);
  const [tab, setTab] = useState("planning");
  const tabsListRef = useRef(null);

  const load = useCallback(() => {
    api.get("/parent/children").then((r) => {
      setChildren(r.data);
      if (r.data.length > 0) setActiveChild((cur) => cur || r.data[0].id);
    });
    api.get("/vacations").then((r) => setVacations(r.data));
    api.get("/meetings").then((r) => setMeetings(r.data));
    api.get("/parent/homeworks").then((r) => setHomeworks(r.data));
    api.get("/parent/attendance").then((r) => setAttendance(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Mobile: scroll active tab into view on change
  useEffect(() => {
    const el = tabsListRef.current;
    if (!el || window.innerWidth >= 768) return;
    const activeEl = el.querySelector('[data-state="active"]');
    if (activeEl && typeof activeEl.scrollIntoView === "function") {
      activeEl.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [tab]);

  const child = children.find((c) => c.id === activeChild) || children[0];
  const childCourses = (child?.courses || []).map((c) => ({ ...c, group_name: child?.group?.name }));

  const childHomeworks = homeworks.find((h) => h.child.request_id === activeChild)?.homeworks || [];
  const pendingCount = childHomeworks.filter((h) => !h.done).length;
  const childAttendance = attendance.find((a) => a.child.request_id === activeChild) || { records: [], stats: { total: 0, present: 0, absent: 0, retard: 0, presence_rate: null } };

  const toggleHomework = async (hw) => {
    try {
      await api.post(`/homeworks/${hw.id}/complete`, { student_request_id: activeChild, done: !hw.done });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12 fade-in" data-testid="parent-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C86444]">Espace Parent</span>
          <h1 className="mt-2 font-[Outfit] font-semibold text-3xl md:text-4xl tracking-tight text-[#1A2547]">Bonjour, {user?.name}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {reenrollmentOpen && (
            <Link to="/parent/reinscription" data-testid="nav-reenroll">
              <Button size="sm" className="rounded-full bg-[#C86444] hover:bg-[#A8533A] text-white h-9">
                <GraduationCap className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Réinscrire pour la nouvelle année <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
              </Button>
            </Link>
          )}
          <NotificationToggle compact />
          <ChangePasswordDialog mustChange={user?.must_change_password} />
          <SignatureGate />
        </div>
      </div>

      {user?.must_change_password && (
        <div className="mt-6 bg-[#FFF5E5] border border-[#C86444]/30 rounded-2xl p-4 text-sm text-[#1A2547] flex items-start gap-2">
          <KeyRound className="w-5 h-5 text-[#C86444] shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <strong>Veuillez changer votre mot de passe</strong>
            <div className="text-[#5c6374]">Pour votre sécurité, modifiez le mot de passe temporaire qui vous a été communiqué.</div>
          </div>
        </div>
      )}

      {children.length === 0 && (
        <div className="mt-10 bg-white border border-[#EAE6DF] rounded-2xl p-10 text-center text-[#5c6374]">
          Aucun enfant inscrit pour le moment.
        </div>
      )}

      {children.length > 0 && (
        <>
          {/* Children selector */}
          {children.length > 1 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {children.map((c) => (
                <button key={c.id} onClick={() => setActiveChild(c.id)}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors ${activeChild === c.id ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white border-[#EAE6DF] hover:border-[#1E3A8A]"}`}
                  data-testid={`child-tab-${c.id}`}>
                  {c.enf_prenom} {c.enf_nom}
                </button>
              ))}
            </div>
          )}

          {child && (
            <>
              <div className="mt-8 bg-white border border-[#EAE6DF] rounded-2xl p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h2 className="font-[Outfit] font-semibold text-2xl text-[#1A2547]">{child.enf_prenom} {child.enf_nom}</h2>
                    <p className="text-sm text-[#5c6374] mt-1">
                      Groupe : <strong>{child.group?.name || "—"}</strong> · Matières : {child.assigned_matieres?.map((m) => ML[m]).join(", ")}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {child.group ? (
                      <Badge className="bg-[#E0E7FF] text-[#1E3A8A] hover:bg-[#E0E7FF] border-0 rounded-full">{child.group.name}</Badge>
                    ) : (
                      <Badge className="bg-[#FFF5E5] text-[#C86444] hover:bg-[#FFF5E5] border-0 rounded-full">Groupe à attribuer</Badge>
                    )}
                  </div>
                </div>
              </div>

              <Tabs value={tab} onValueChange={setTab} orientation="vertical" className="mt-8 flex flex-col md:flex-row md:gap-6">
                <TabsList ref={tabsListRef} className="mobile-tabs-fade bg-[#F4F1EB] p-1.5 md:p-2 flex md:flex-col md:items-stretch md:w-60 md:shrink-0 md:h-auto md:rounded-2xl md:sticky md:top-24 md:self-start rounded-full h-auto overflow-x-auto md:overflow-visible gap-1 justify-start max-w-full">
                  <TabsTrigger value="planning" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="parent-tab-planning">
                    <Cal className="w-4 h-4 mr-1.5" /> Planning
                  </TabsTrigger>
                  <TabsTrigger value="homework" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="parent-tab-homework">
                    <BookOpenCheck className="w-4 h-4 mr-1.5" /> Devoirs
                    {pendingCount > 0 && <Badge className="ml-2 bg-[#C86444] text-white border-0 rounded-full px-1.5 py-0 text-[10px] h-4">{pendingCount}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="attendance" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="parent-tab-attendance">
                    <ClipboardList className="w-4 h-4 mr-1.5" /> Présences
                    {childAttendance.stats.absent > 0 && <Badge className="ml-2 bg-[#B0413E] text-white border-0 rounded-full px-1.5 py-0 text-[10px] h-4">{childAttendance.stats.absent}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="messages" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="parent-tab-messages">
                    <MessageSquare className="w-4 h-4 mr-1.5" /> Messagerie
                  </TabsTrigger>
                </TabsList>

                <div className="flex-1 min-w-0">
                <TabsContent value="planning" className="mt-6 md:mt-0">
                  <CalendarView courses={childCourses} vacations={vacations} meetings={meetings} groupName={() => child.group?.name} initialMode="week" />
                </TabsContent>

                <TabsContent value="homework" className="mt-6 md:mt-0 space-y-3">
                  {childHomeworks.length === 0 && <div className="bg-white border border-[#EAE6DF] rounded-2xl p-10 text-center text-[#5c6374]">Aucun devoir pour le moment.</div>}
                  {childHomeworks.map((hw) => <HomeworkCard key={hw.id} hw={hw} onToggle={() => toggleHomework(hw)} />)}
                </TabsContent>

                <TabsContent value="attendance" className="mt-6 md:mt-0">
                  <AttendancePanel data={childAttendance} />
                </TabsContent>

                <TabsContent value="messages" className="mt-6 md:mt-0">
                  <MessagingPanel currentUser={user} />
                </TabsContent>
                </div>
              </Tabs>
            </>
          )}
        </>
      )}
    </div>
  );
}

function HomeworkCard({ hw, onToggle }) {
  const overdue = hw.due_date && new Date(hw.due_date) < new Date() && !hw.done;
  return (
    <div className={`bg-white border rounded-2xl p-5 ${hw.done ? "border-[#1E7A4D]/30 bg-[#E0F2E9]/30" : overdue ? "border-[#C86444]/30 bg-[#FFF5E5]/30" : "border-[#EAE6DF]"}`} data-testid={`homework-${hw.id}`}>
      <div className="flex items-start gap-4">
        <Checkbox checked={hw.done} onCheckedChange={onToggle} className="mt-1" data-testid={`hw-toggle-${hw.id}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`font-[Outfit] font-semibold text-lg ${hw.done ? "line-through text-[#8a92a3]" : "text-[#1A2547]"}`}>{hw.title}</h3>
            <Badge className="bg-[#E0E7FF] text-[#1E3A8A] border-0 rounded-full">{ML[hw.matiere] || hw.matiere}</Badge>
            {hw.audience === "individual" && <Badge className="bg-[#FBE8DC] text-[#C86444] border-0 rounded-full">Personnel</Badge>}
            {overdue && <Badge className="bg-[#FBE5E4] text-[#B0413E] border-0 rounded-full">En retard</Badge>}
            {hw.done && <Badge className="bg-[#E0F2E9] text-[#1E7A4D] border-0 rounded-full"><CheckCircle2 className="w-3 h-3 mr-1" />Fait</Badge>}
          </div>
          {hw.description && <p className="text-sm text-[#5c6374] mt-2 leading-relaxed whitespace-pre-wrap">{hw.description}</p>}
          <div className="text-xs text-[#8a92a3] mt-3">À rendre pour le {new Date(hw.due_date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
          {hw.attachment_path && (
            <a href={`${API}/files/${hw.attachment_path}`} target="_blank" rel="noreferrer" className="text-xs text-[#1E3A8A] underline mt-1 inline-block">Pièce jointe</a>
          )}
        </div>
      </div>
    </div>
  );
}

function AttendancePanel({ data }) {
  const STATUS = {
    present: { label: "Présent", style: "bg-[#E0F2E9] text-[#1E7A4D]" },
    absent: { label: "Absent", style: "bg-[#FBE5E4] text-[#B0413E]" },
    retard: { label: "Retard", style: "bg-[#FFF5E5] text-[#C86444]" },
  };
  const { stats, records } = data;
  const tiles = [
    { label: "Taux présence", value: stats.presence_rate != null ? `${stats.presence_rate}%` : "—", color: "#1E3A8A" },
    { label: "Présent", value: stats.present, color: "#1E7A4D" },
    { label: "Retards", value: stats.retard, color: "#C86444" },
    { label: "Absences", value: stats.absent, color: "#B0413E" },
  ];
  return (
    <div data-testid="parent-attendance-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <div key={t.label} className="bg-white border border-[#EAE6DF] rounded-2xl p-5">
            <div className="text-2xl md:text-3xl font-[Outfit] font-semibold" style={{ color: t.color }}>{t.value}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a92a3] mt-1.5">{t.label}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 bg-white border border-[#EAE6DF] rounded-2xl p-6">
        <h3 className="font-[Outfit] font-semibold text-lg text-[#1A2547] mb-4">Détail des séances</h3>
        {records.length === 0 && <div className="text-sm text-[#5c6374] py-6 text-center">Aucune présence enregistrée pour le moment.</div>}
        <div className="space-y-2">
          {records.map((r) => (
            <div key={r.id || `${r.student_id}-${r.date}-${r.matiere}`} className="flex items-start gap-3 p-3 rounded-xl border border-[#EAE6DF] bg-[#FCFBF8]" data-testid={`parent-attendance-row-${r.id}`}>
              <Badge className={`border-0 rounded-full shrink-0 ${STATUS[r.status]?.style}`}>{STATUS[r.status]?.label}</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#1A2547]">
                  {new Date(r.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                  {r.matiere && <span className="text-[#8a92a3]"> · {ML[r.matiere] || r.matiere}</span>}
                </div>
                {r.note && <div className="text-xs text-[#5c6374] mt-1 italic">Motif : « {r.note} »</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
